import React, { useState } from 'react';
import Select from './Select';


const DateRangeSelector = ({ value, onChange, onClose, className = "" }) => {
  const [isCustomRange, setIsCustomRange] = useState(value?.preset === 'custom');
  const [customStartDate, setCustomStartDate] = useState(value?.preset === 'custom' ? value?.startDate : '');
  const [customEndDate, setCustomEndDate] = useState(value?.preset === 'custom' ? value?.endDate : '');

  const presetOptions = [
    { value: '7', label: '7 days' },
    { value: '14', label: '14 days' },
    { value: '30', label: '30 days' },
    { value: 'custom', label: 'Custom dates' }
  ];

  const handlePresetChange = (selectedValue) => {
    if (selectedValue === 'custom') {
      setIsCustomRange(true);
      // Don't change date range until custom dates are selected
    } else {
      setIsCustomRange(false);
      const endDate = new Date();
      const startDate = new Date();
      startDate?.setDate(endDate?.getDate() - parseInt(selectedValue));
      
      onChange({
        startDate: startDate?.toISOString()?.split('T')?.[0],
        endDate: endDate?.toISOString()?.split('T')?.[0],
        preset: selectedValue
      });
      
      if (onClose) onClose();
    }
  };

  const handleCustomDateChange = () => {
    if (customStartDate && customEndDate) {
      // Validate that start date is before end date
      const start = new Date(customStartDate);
      const end = new Date(customEndDate);
      
      if (start <= end) {
        onChange({
          startDate: customStartDate,
          endDate: customEndDate,
          preset: 'custom'
        });
        
        if (onClose) onClose();
      } else {
        alert('Start date must be before end date');
      }
    }
  };

  const handleKeyPress = (e) => {
    if (e?.key === 'Enter' && isCustomRange) {
      handleCustomDateChange();
    }
  };

  const formatDateRange = () => {
    if (!value) return 'Select range';
    
    const start = new Date(value.startDate);
    const end = new Date(value.endDate);
    
    const formatDate = (date) => {
      return date?.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    };
    
    return `${formatDate(start)} - ${formatDate(end)}`;
  };

  return (
    <div className={`bg-surface border border-border rounded-lg shadow-lg p-4 min-w-[300px] ${className}`}>
      <div className="space-y-4">
        <div>
          <h4 className="text-sm font-medium text-text-primary mb-3">Select Date Range</h4>
          <div className="grid grid-cols-2 gap-2">
            {presetOptions?.map((option) => (
              <button
                key={option?.value}
                onClick={() => handlePresetChange(option?.value)}
                className={`px-3 py-2 text-sm rounded-md border transition-colors ${
                  value?.preset === option?.value
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-background text-text-secondary border-border hover:bg-muted'
                }`}
              >
                {option?.label}
              </button>
            ))}
          </div>
        </div>
        
        {isCustomRange && (
          <div className="space-y-3">
            <div className="border-t border-border pt-3">
              <label className="block text-xs font-medium text-text-secondary mb-2">
                Custom Date Range
              </label>
              <div className="space-y-2">
                <div>
                  <label className="block text-xs text-text-secondary mb-1">From</label>
                  <input
                    type="date"
                    value={customStartDate}
                    onChange={(e) => setCustomStartDate(e?.target?.value)}
                    onKeyPress={handleKeyPress}
                    className="w-full px-3 py-2 text-sm border border-border rounded-md bg-input focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                </div>
                <div>
                  <label className="block text-xs text-text-secondary mb-1">To</label>
                  <input
                    type="date"
                    value={customEndDate}
                    onChange={(e) => setCustomEndDate(e?.target?.value)}
                    onKeyPress={handleKeyPress}
                    className="w-full px-3 py-2 text-sm border border-border rounded-md bg-input focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                </div>
                <button
                  onClick={handleCustomDateChange}
                  disabled={!customStartDate || !customEndDate}
                  className="w-full px-3 py-2 text-sm bg-primary text-primary-foreground rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-primary/90 transition-colors"
                >
                  Apply Custom Range
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      {value && (
        <div className="mt-4 pt-3 border-t border-border">
          <div className="text-xs text-text-secondary">
            Selected: <span className="font-medium text-text-primary">{formatDateRange()}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default DateRangeSelector;